import { CSSProperties, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { AgenticViewer } from '@amzn/work-spaces-agentic-web-viewer';
import '@amzn/work-spaces-agentic-web-viewer/styles.css';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { useReportHeartbeatTask } from '@/hooks/useReportHeartbeatTask';
import { useSessionStore } from '@/stores/session';
import { isAgenticWorkspace } from '@views/Stream/agentic';

// The viewer logs through the client's logger, so its lines reach the upload.
const logger = CoreFactory.getLogger();

// The docking shell needs a fixed box that page reflow cannot disturb.
const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 1000,
};

const hasWspContextHandoff = (): boolean => {
  try {
    return window.sessionStorage.getItem('wsp_context') !== null;
  } catch {
    // Storage is unavailable, so there is no handoff to read.
    return false;
  }
};

// With no wsp_context handoff, or on a standard WorkSpace, there is no agent.
const AgenticRoute: React.FC = () => {
  // Persisted in sessionStorage, so it survives a reload of /agentic.
  const resourceId = useSessionStore((state) => state.resourceId);
  // Routing only. The WorkSpace host enforces access.
  const resourceDetails = useSessionStore(
    (state) => state.resource?.ResourceDetails
  );
  const routeShowsViewer =
    hasWspContextHandoff() && isAgenticWorkspace(resourceDetails);
  const { startReportingHeartbeat } = useReportHeartbeatTask();

  // wsp.html reports the heartbeat on the standard path. Without it, the broker
  // ends a SAML or IAM Identity Center session about 25 minutes after sign-in.
  useEffect(() => {
    if (routeShowsViewer) {
      startReportingHeartbeat();
    }
  }, [routeShowsViewer]);

  if (!routeShowsViewer) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={containerStyle}>
      <AgenticViewer workspaceId={resourceId || undefined} logger={logger} />
    </div>
  );
};

export default AgenticRoute;
